import {DragEvent, DragEventHandler, useMemo} from 'react'
import {log} from 'common/log'
import {SlctInternalProps, SlctOption} from 'module/Slct/type/SlctType'

const SLCT_INDEX_PREFIX = 'slctindex'
function getDragAndDropIndices(evt: DragEvent<HTMLDivElement>): { currentIndex: number, nextIndex: number } {
	const data = evt.dataTransfer.getData('text')
	if (!data) return {currentIndex: NaN, nextIndex: NaN}
	const currentIndex = data.startsWith(SLCT_INDEX_PREFIX) ? +data.substring(SLCT_INDEX_PREFIX.length) : NaN
	const nextIndex = Number(evt.currentTarget.dataset['slctindex'])
	return {currentIndex, nextIndex}
}

interface Args<ModelType extends object | void, ValueType extends string | number> {
	option: SlctOption<ModelType, ValueType>
	index: number
	onMoveOption: SlctInternalProps<ModelType, ValueType>['onMoveOption']
	disabled: boolean | undefined
}

export function useSlctDraggable<ModelType extends object | void, ValueType extends string | number>({option, index, onMoveOption, disabled}: Args<ModelType, ValueType>) {
	const draggable = Boolean(!disabled && !option.disabled && option.active && onMoveOption)
	if (!draggable) log.trace('Slct draggable disabled', option.disabled, option.active, onMoveOption)

	const onDragStart: DragEventHandler<HTMLDivElement> | undefined = useMemo(() => !draggable ? undefined : (evt: DragEvent<HTMLDivElement>) => {
		log.trace('Slct onDragStart', evt, index)
		evt.dataTransfer.setData('text', SLCT_INDEX_PREFIX + index)
		evt.dataTransfer.effectAllowed = 'move'
		evt.dataTransfer.dropEffect = 'move'
		evt.currentTarget.style.opacity = '0.4';
	}, [draggable, index])

	const onDrop: DragEventHandler<HTMLDivElement> | undefined = useMemo(() => !draggable ? undefined : (evt) => {
		evt.currentTarget.classList.remove('SlctDragOver')
		const {currentIndex, nextIndex} = getDragAndDropIndices(evt)
		log.trace('Slct onDrop', index, currentIndex, nextIndex, evt)
		if (!onMoveOption || isNaN(currentIndex) || isNaN(nextIndex)) return;
		onMoveOption(currentIndex, nextIndex, evt) // move to end if there is no slctindex
	}, [draggable, onMoveOption, index])

	const onDragEnd: DragEventHandler<HTMLDivElement> | undefined = useMemo(() => !draggable ? undefined : (evt: DragEvent<HTMLDivElement>) => {
		log.trace('Slct onDragEnd', evt)
		evt.currentTarget.style.opacity = '';
		evt.currentTarget.classList.remove('SlctDragOver')
	}, [draggable])

	const onDragOver: DragEventHandler<HTMLDivElement> | undefined = useMemo(() => !draggable ? undefined : (evt: DragEvent<HTMLDivElement>) => {
		log.trace('Slct onDragOver', evt)
		if (!evt.currentTarget.classList.contains('SlctDragOver')) evt.currentTarget.classList.add('SlctDragOver')
		evt.preventDefault()
		evt.nativeEvent.preventDefault()
		return false
	}, [draggable])

	const onDragEnter: DragEventHandler<HTMLDivElement> | undefined = useMemo(() => !draggable ? undefined : (evt: DragEvent<HTMLDivElement>) => {
		log.trace('Slct onDragEnter', evt)
		if (!evt.currentTarget.classList.contains('SlctDragOver')) evt.currentTarget.classList.add('SlctDragOver')
		evt.preventDefault()
		evt.nativeEvent.preventDefault()
		return false
	}, [draggable])

	const onDragLeave: DragEventHandler<HTMLDivElement> | undefined = useMemo(() => !draggable ? undefined : (evt: DragEvent<HTMLDivElement>) => {
		log.trace('Slct onDragLeave', evt)
		evt.currentTarget.classList.remove('SlctDragOver')
	}, [draggable])

	return {
		className: draggable ? 'SlctOptionMovable' : '',
		draggable: draggable,
		onDragStart: onDragStart,
		onDragEnd: onDragEnd,
		onDragOver: onDragOver,
		onDragEnter: onDragEnter,
		onDragLeave: onDragLeave,
		onDrop: onDrop,
		'data-slctindex': index,
	}
}